import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { systemsApi } from "../service/systemsApi";
import { ApiError } from "../service/apiClient";
import { StatusBadge } from "../components/StatusBadge";
import { KomodoBadge } from "../components/KomodoBadge";
import { SystemFormModal } from "../components/SystemFormModal";
import { HOSTING_TYPE_LABELS, SYSTEM_TYPE_LABELS, formatRelativeTime } from "../lib/hostingLabels";
import { getSslTooltipText } from "../lib/checkMetadata";
import styles from "./SystemDetailPage.module.scss";
export function SystemDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [system, setSystem] = useState(null);
    const [history, setHistory] = useState([]);
    const [showEdit, setShowEdit] = useState(false);
    const [checking, setChecking] = useState(false);
    const [checkingKomodo, setCheckingKomodo] = useState(false);
    const [komodoError, setKomodoError] = useState(null);
    const load = useCallback(async () => {
        if (!id)
            return;
        const [systemResult, historyResult] = await Promise.all([systemsApi.get(id), systemsApi.history(id, 50)]);
        setSystem(systemResult);
        setHistory(historyResult);
    }, [id]);
    useEffect(() => {
        load();
    }, [load]);
    if (!system) {
        return _jsx("p", { className: styles.loading, children: "Laddar\u2026" });
    }
    async function handleCheckNow() {
        if (!id)
            return;
        setChecking(true);
        try {
            await systemsApi.checkNow(id);
            await load();
        }
        finally {
            setChecking(false);
        }
    }
    async function handleCheckKomodoNow() {
        if (!id)
            return;
        setCheckingKomodo(true);
        setKomodoError(null);
        try {
            const updated = await systemsApi.checkKomodoNow(id);
            setSystem(updated);
        }
        catch (err) {
            setKomodoError(err instanceof ApiError ? err.message : "Kunde inte kontrollera Komodo.");
        }
        finally {
            setCheckingKomodo(false);
        }
    }
    async function handleUpdate(payload) {
        if (!id)
            return;
        await systemsApi.update(id, payload);
        setShowEdit(false);
        await load();
    }
    async function handleDelete() {
        if (!id || !confirm(`Ta bort "${system === null || system === void 0 ? void 0 : system.name}"? Detta går inte att ångra.`))
            return;
        await systemsApi.remove(id);
        navigate("/");
    }
    return (_jsxs("div", { className: styles.page, children: [_jsxs("div", { className: styles.header, children: [_jsxs("div", { children: [_jsx("h1", { children: system.name }), _jsxs("div", { className: styles.badges, children: [_jsx(StatusBadge, { status: system.currentStatus, title: getSslTooltipText(system.lastCheckMetadata) }), _jsx(KomodoBadge, { system: system })] })] }), _jsxs("div", { className: styles.headerActions, children: [_jsx("button", { type: "button", onClick: handleCheckNow, disabled: checking, children: checking ? "Kontrollerar…" : "Kontrollera nu" }), system.komodoStackName && (_jsx("button", { type: "button", onClick: handleCheckKomodoNow, disabled: checkingKomodo, children: checkingKomodo ? "Kontrollerar Komodo…" : "Kontrollera Komodo nu" })), _jsx("button", { type: "button", onClick: () => setShowEdit(true), children: "Redigera" }), _jsx("button", { type: "button", className: styles.danger, onClick: handleDelete, children: "Ta bort" })] })] }), komodoError && _jsx("p", { className: styles.komodoError, children: komodoError }), system.description && _jsx("p", { children: system.description }), _jsxs("dl", { className: styles.meta, children: [_jsxs("div", { children: [_jsx("dt", { children: "URL" }), _jsx("dd", { children: _jsx("a", { href: system.url, target: "_blank", rel: "noreferrer", children: system.url }) })] }), system.adminUrl && (_jsxs("div", { children: [_jsx("dt", { children: "Driftsida" }), _jsx("dd", { children: _jsx("a", { href: system.adminUrl, target: "_blank", rel: "noreferrer", children: system.adminUrl }) })] })), _jsxs("div", { children: [_jsx("dt", { children: "Typ" }), _jsx("dd", { children: SYSTEM_TYPE_LABELS[system.systemType] })] }), _jsxs("div", { children: [_jsx("dt", { children: "Hosting" }), _jsx("dd", { children: HOSTING_TYPE_LABELS[system.hostingType] })] }), _jsxs("div", { children: [_jsx("dt", { children: "Kontrollintervall" }), _jsxs("dd", { children: [system.checkIntervalSeconds, "s"] })] }), _jsxs("div", { children: [_jsx("dt", { children: "Senast kontrollerad" }), _jsx("dd", { children: formatRelativeTime(system.lastCheckedAt) })] }), system.komodoStackName && (_jsxs(_Fragment, { children: [_jsxs("div", { children: [_jsx("dt", { children: "Komodo stack" }), _jsx("dd", { children: system.komodoStackName })] }), _jsxs("div", { children: [_jsx("dt", { children: "Komodo senast kontrollerad" }), _jsx("dd", { children: formatRelativeTime(system.komodoLastCheckedAt) })] })] }))] }), _jsx("h2", { children: "Historik" }), history.length === 0 ? (_jsx("p", { className: styles.loading, children: "Ingen historik \u00E4nnu." })) : (_jsxs("table", { className: styles.history, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Tidpunkt" }), _jsx("th", { children: "Resultat" }), _jsx("th", { children: "Statuskod" }), _jsx("th", { children: "Svarstid" }), _jsx("th", { children: "Fel" })] }) }), _jsx("tbody", { children: history.map((entry) => {
                            var _a, _b;
                            return (_jsxs("tr", { children: [_jsx("td", { children: new Date(entry.checkedAt).toLocaleString("sv-SE") }), _jsx("td", { children: _jsx(StatusBadge, { status: entry.isUp ? "Up" : "Down", title: getSslTooltipText(entry.metadata) }) }), _jsx("td", { children: (_a = entry.statusCode) !== null && _a !== void 0 ? _a : "–" }), _jsx("td", { children: entry.responseTimeMs !== null ? `${entry.responseTimeMs} ms` : "–" }), _jsx("td", { children: (_b = entry.errorMessage) !== null && _b !== void 0 ? _b : "–" })] }, entry.id));
                        }) })] })), showEdit && _jsx(SystemFormModal, { initial: system, onSubmit: handleUpdate, onClose: () => setShowEdit(false) })] }));
}
